/**
 * FilesTrayContent - Content panel for the files tray
 *
 * Shows TreeNavigator on the left and file viewer on the right.
 * Expands TreeNavigator to full width when searching.
 */

import { ChevronLeft, Files, X } from "lucide-react"
import { observer } from "mobx-react"
import { useEffect, useState } from "react"
import type { FileBrowserManager } from "../../store/managers/FileBrowserManager"
import { FileContentViewer } from "../FileContentViewer"
import { FilePathBar } from "../FilePathBar"
import { FileTabs } from "../FileTabs"
import { TreeNavigator } from "../TreeNavigator"

interface FilesTrayContentProps {
    fileBrowser: FileBrowserManager
    taskId: string
    onClose: () => void
    compact?: boolean
}

export const FilesTrayContent = observer(function FilesTrayContent({ fileBrowser, taskId, onClose, compact = false }: FilesTrayContentProps) {
    const isSearching = fileBrowser.isSearching
    const [detailOpen, setDetailOpen] = useState(false)
    const [tabsOpen, setTabsOpen] = useState(false)

    useEffect(() => {
        if (!compact) {
            setDetailOpen(false)
            setTabsOpen(false)
        }
    }, [compact])

    if (compact) {
        if (detailOpen && fileBrowser.activeFile) {
            return (
                <div className="flex h-full flex-col">
                    <FilePathBar
                        path={fileBrowser.activeFile}
                        workingDir={fileBrowser.workingDir}
                        compact
                        leading={
                            <button
                                type="button"
                                onClick={() => {
                                    setDetailOpen(false)
                                    setTabsOpen(false)
                                }}
                                className="btn flex h-10 w-10 items-center justify-center text-muted hover:bg-base-200 hover:text-base-content"
                                aria-label="Back to files"
                            >
                                <ChevronLeft size={18} />
                            </button>
                        }
                        trailing={
                            fileBrowser.openTabs.length > 1 ? (
                                <button
                                    type="button"
                                    onClick={() => setTabsOpen((value) => !value)}
                                    className="btn flex h-10 w-10 items-center justify-center text-muted hover:bg-base-200 hover:text-base-content"
                                    aria-label="Open files"
                                    aria-expanded={tabsOpen}
                                >
                                    {tabsOpen ? <X size={17} /> : <Files size={17} />}
                                </button>
                            ) : null
                        }
                    />
                    {tabsOpen && (
                        <div className="shrink-0 border-b border-border bg-base-200">
                            {fileBrowser.openTabs.map((tab) => (
                                <button
                                    key={tab.path}
                                    type="button"
                                    className="btn flex min-h-11 w-full items-center border-b border-border px-4 text-left font-mono text-xs text-base-content last:border-b-0 hover:bg-base-300"
                                    onClick={() => {
                                        fileBrowser.switchToTab(tab.path)
                                        setTabsOpen(false)
                                    }}
                                >
                                    <span className="truncate">{tab.name}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    <FileContentViewer fileBrowser={fileBrowser} taskId={taskId} className="min-h-0 flex-1" showPathBar={false} />
                </div>
            )
        }

        return <TreeNavigator fileBrowser={fileBrowser} className="h-full border-r-0" onEscapeClose={onClose} onFileOpen={() => setDetailOpen(true)} compact />
    }

    return (
        <div className="flex h-full">
            <TreeNavigator fileBrowser={fileBrowser} className={isSearching ? "flex-1" : "w-64 flex-shrink-0 border-r border-border"} onEscapeClose={onClose} />
            {!isSearching && (
                <div className="flex-1 min-w-0 flex flex-col">
                    <FileTabs fileBrowser={fileBrowser} />
                    <FileContentViewer fileBrowser={fileBrowser} taskId={taskId} className="flex-1 min-h-0" />
                </div>
            )}
        </div>
    )
})
